import React, { useState, useEffect } from 'react';

import { templatesAPI } from '../services/api';
import Skeleton from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import ErrorState from '../components/common/ErrorState';
import ButtonSpinner from '../components/common/ButtonSpinner';

const Templates = () => {
    // Theme context hook - isDark is handled via Tailwind classes
    // const { isDark } = useTheme();

    // State
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'whatsapp', 'sms', 'email'
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [usage, setUsage] = useState({ current: 0, limit: -1, allowed: true });

    const [formData, setFormData] = useState({
        template_name: '',
        channel_type: 'whatsapp',
        subject: '',
        message_body: '',
        variables: []
    });

    // Strip HTML tags from legacy templates (previously stored as full HTML)
    const stripHtml = (html) => {
        if (!html) return '';
        if (!html.includes('<')) return html;
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const raw = doc.body?.innerText || doc.body?.textContent || html.replace(/<[^>]+>/g, '');
            // Normalize whitespace
            const normalized = raw
                .split('\n')
                .map(line => line.replace(/[ \t]+/g, ' ').trim())
                .join('\n')
                .replace(/\n{3,}/g, '\n\n')
                .trim();
            // Remove layout footer lines injected by getBaseLayout()
            const footerPatterns = [
                /^OffTrigger$/m,
                /^⚡$/m,
                /^©\s*\d{4}\s*OffTrigger\./m,
                /^Privacy Policy\s*•?/m,
                /^Terms\s*•?/m,
                /^Support$/m,
            ];
            let cleaned = normalized;
            for (const pattern of footerPatterns) {
                cleaned = cleaned.replace(pattern, '');
            }
            return cleaned.replace(/\n{3,}/g, '\n\n').trim();
        } catch {
            return html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        }
    };

    // Fetch Templates
    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await templatesAPI.getTemplates();
            // Ensure we extract the array correctly from the response
            const responseData = response.data?.templates || response.data || [];

            if (response.data?.usage) {
                setUsage(response.data.usage);
            }

            if (Array.isArray(responseData)) {
                setTemplates(responseData);
            } else {
                console.error('API returned non-array:', responseData);
                setTemplates([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setError(error.message || 'Failed to load templates. Please check your connection.');
            setLoading(false);
        }
        // Demo data fallback for UI testing if API fails or returns empty
        // setTemplates([
        //     { _id: '1', template_name: 'Welcome Message', channel_type: 'whatsapp', message_body: 'Hi {leadName}, thanks for your interest in {propertyAddress}.', is_active: true, usage_count: 12 },
        //     { _id: '2', template_name: 'Follow Up', channel_type: 'sms', message_body: 'Just checking in on your search.', is_active: true, usage_count: 5 },
        // ]);
    };

    // Filter Logic
    const filteredTemplates = (activeFilter === 'all'
        ? templates
        : (Array.isArray(templates) ? templates.filter(t => t.channel_type === activeFilter) : [])) || [];

    // Refs
    const textareaRef = React.useRef(null);

    // Form Handling
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleOpenModal = (template = null) => {
        if (!template && !usage.allowed && usage.limit !== -1) {
            alert(`You have reached your limit of ${usage.limit} templates. Please upgrade to add more.`);
            return;
        }

        if (template) {
            setEditingTemplate(template);
            const rawBody = template.message_body || '';
            // Strip HTML from legacy templates that had full HTML stored
            const cleanBody = template.channel_type === 'email' ? stripHtml(rawBody) : rawBody;
            setFormData({
                template_name: template.template_name || '',
                channel_type: template.channel_type || 'whatsapp',
                subject: template.subject || '',
                message_body: cleanBody,
                variables: template.variables || []
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                template_name: '',
                channel_type: 'whatsapp',
                subject: '',
                message_body: '',
                variables: []
            });
        }
        setShowCreateModal(true);
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditingTemplate(null);
    };

    const handleSubmit = async () => {
        try {
            if (!formData.template_name || !formData.message_body) {
                alert('Please fill in required fields');
                return;
            }

            setIsSaving(true);
            if (editingTemplate) {
                await templatesAPI.updateTemplate(editingTemplate._id || editingTemplate.id, formData);
            } else {
                await templatesAPI.createTemplate(formData);
            }

            handleCloseModal();
            fetchTemplates();
        } catch (err) {
            console.error('Error saving template:', err);
            if (err.response?.status === 403) {
                alert(err.response.data.error || 'Limit reached');
            } else {
                alert('Failed to save template');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this template?')) return;
        try {
            setDeletingId(id);
            await templatesAPI.deleteTemplate(id);
            fetchTemplates();
        } catch (err) {
            console.error('Error deleting template:', err);
            alert('Failed to delete template');
        } finally {
            setDeletingId(null);
        }
    };

    const handleSetDefault = async (id) => {
        try {
            await templatesAPI.setDefaultTemplate(id);
            fetchTemplates();
        } catch (err) {
            console.error('Error setting default:', err);
        }
    };

    const handleDuplicate = (template) => {
        if (!usage.allowed && usage.limit !== -1) {
            alert(`Cannot duplicate: Template limit reached (${usage.limit}). Please upgrade.`);
            return;
        }

        setEditingTemplate(null);
        setFormData({
            template_name: `${template.template_name} (Copy)`,
            channel_type: template.channel_type,
            subject: template.subject,
            message_body: template.message_body,
            variables: template.variables
        });
        setShowCreateModal(true);
    };

    const insertVariable = (variable) => {
        if (textareaRef.current) {
            const textarea = textareaRef.current;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = formData.message_body;

            const newText = text.substring(0, start) + variable + text.substring(end);
            setFormData(prev => ({ ...prev, message_body: newText }));

            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + variable.length;
                textarea.focus();
            }, 0);
        } else {
            setFormData(prev => ({
                ...prev,
                message_body: prev.message_body + variable
            }));
        }
    };

    const insertCTA = () => {
        const ctaText = `\n---\nReady to move forward?\nBook a Call: {calendarLink}\n---\n`;
        insertVariable(ctaText);
    };

    // UI Helpers
    const getChannelIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'whatsapp': return { icon: 'chat', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/20' };
            case 'sms': return { icon: 'sms', color: 'text-amber-500', bg: 'bg-amber-500/10 border-amber-500/20' };
            case 'email': return { icon: 'mail', color: 'text-purple-500', bg: 'bg-purple-500/10 border-purple-500/20' };
            default: return { icon: 'stars', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' };
        }
    };

    if (loading && templates.length === 0) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen p-6 md:p-8">
                <div className="flex flex-col md:flex-row justify-between mb-8 gap-4">
                    <div className="w-1/3">
                        <Skeleton.Text lines={2} />
                    </div>
                    <div className="w-48 h-10 bg-card rounded-lg border border-white/5 animate-pulse"></div>
                </div>
                <div className="h-10 w-full mb-8 bg-card rounded-lg animate-pulse border border-white/5"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map(i => <Skeleton.Card key={i} />)}
                </div>
            </div>
        );
    }

    if (error && templates.length === 0) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen p-6 md:p-12 flex items-center justify-center">
                <ErrorState message={error} onRetry={fetchTemplates} className="w-full max-w-lg" />
            </div>
        );
    }

    return (
        <div className="bg-mint dark:bg-navy min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">Templates</h2>
                    <p className="text-darkTeal/60 dark:text-light/60 text-left">Manage your auto-response message templates.</p>
                </div>
                <div className="flex gap-3 items-center">
                    {usage.limit !== -1 && (
                        <div className={`px-4 py-2 rounded-lg text-sm font-bold border ${!usage.allowed ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-teal/5 border-teal/10 text-darkTeal/60 dark:text-light/60'}`}>
                            {usage.current} / {usage.limit} Used
                        </div>
                    )}
                    <button
                        onClick={() => handleOpenModal()}
                        disabled={!usage.allowed && usage.limit !== -1}
                        className={`px-5 py-2.5 font-bold rounded-lg shadow-[0_0_15px_rgba(37,176,155,0.3)] dark:shadow-[0_0_15px_rgba(8,217,214,0.3)] transition-all flex items-center gap-2 ${!usage.allowed && usage.limit !== -1
                            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-primary dark:bg-cyan hover:bg-primary/80 dark:hover:bg-cyan/80 text-white dark:text-navy hover:shadow-[0_0_20px_rgba(37,176,155,0.5)] dark:hover:shadow-[0_0_20px_rgba(8,217,214,0.5)]'
                            }`}
                        title={!usage.allowed && usage.limit !== -1 ? "Template limit reached" : "Create New Template"}
                    >
                        <span className="material-symbols-outlined text-lg">add</span>
                        Create Template
                    </button>
                </div>
            </div>

            {/* Error Banner (if data exists) */}
            {error && templates.length > 0 && (
                <div className="mb-8">
                    <ErrorState message={error} onRetry={fetchTemplates} />
                </div>
            )}

            {/* Limit Warning Banner */}
            {!usage.allowed && usage.limit !== -1 && (
                <div className="mb-8 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-amber-500 text-xl">warning</span>
                        <div>
                            <p className="font-bold text-darkTeal dark:text-light text-sm text-left">Template Limit Reached</p>
                            <p className="text-xs text-darkTeal/60 dark:text-light/60 text-left">You have reached the limit of {usage.limit} templates for your plan.</p>
                        </div>
                    </div>
                    <a href="/billing" className="px-4 py-2 bg-amber-500 text-white text-xs font-bold uppercase rounded-lg hover:bg-amber-600 transition-colors">
                        Upgrade
                    </a>
                </div>
            )}

            {/* Tabs */}
            <div className="mb-8 border-b border-teal/10 dark:border-light/10 overflow-x-auto">
                <nav className="flex gap-8 min-w-max">
                    {['all', 'whatsapp', 'sms', 'email'].map(type => {
                        const count = type === 'all'
                            ? (Array.isArray(templates) ? templates.length : 0)
                            : (Array.isArray(templates) ? templates.filter(t => t.channel_type === type).length : 0);

                        const isActive = activeFilter === type;

                        return (
                            <button
                                key={type}
                                onClick={() => setActiveFilter(type)}
                                className={`pb-4 px-1 inline-flex items-center gap-2 font-semibold text-sm transition-all border-b-2 ${isActive
                                    ? 'border-primary dark:border-cyan text-primary dark:text-cyan'
                                    : 'border-transparent text-darkTeal/60 dark:text-light/60 hover:text-darkTeal dark:hover:text-light hover:border-teal/30 dark:hover:border-light/30'
                                    }`}
                            >
                                <span className="capitalize">{type === 'all' ? 'All Templates' : type === 'sms' ? 'SMS' : type}</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isActive
                                    ? 'bg-primary/20 dark:bg-cyan/20 text-primary dark:text-cyan'
                                    : 'bg-teal/10 dark:bg-light/10 text-darkTeal/60 dark:text-light/60'
                                    }`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
                {filteredTemplates.map(template => {
                    const style = getChannelIcon(template.channel_type);
                    return (
                        <div key={template._id || template.id} className="bg-white dark:bg-navy rounded-xl p-5 border border-teal/20 dark:border-light/20 hover:border-primary/50 dark:hover:border-cyan/50 shadow-sm hover:shadow-[0_4px_20px_rgba(37,176,155,0.1)] dark:hover:shadow-[0_4px_20px_rgba(8,217,214,0.1)] transition-all group flex flex-col h-full text-left">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <span className={`p-2 rounded-lg border ${style.bg} ${style.color}`}>
                                        <span className="material-symbols-outlined text-xl">{style.icon}</span>
                                    </span>
                                    <div>
                                        <span className="text-xs text-darkTeal/50 dark:text-light/50 font-bold uppercase tracking-wider block mb-0.5">{template.channel_type}</span>
                                        {template.is_default && (
                                            <span className="bg-primary/10 dark:bg-cyan/10 text-primary dark:text-cyan text-[10px] font-bold px-2 py-0.5 rounded border border-primary/30 dark:border-cyan/30">DEFAULT</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleSetDefault(template._id || template.id)} className="text-darkTeal/30 dark:text-light/30 hover:text-primary dark:hover:text-cyan transition-colors" title="Set Default">
                                        <span className={`material-symbols-outlined text-lg ${template.is_default ? 'text-primary dark:text-cyan filled' : ''}`}>star</span>
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-darkTeal dark:text-light mb-3 truncate">{template.template_name}</h3>

                            <div className="bg-mint dark:bg-navy/50 border border-teal/10 dark:border-light/10 rounded-lg p-3 mb-4 flex-1 text-sm text-darkTeal/70 dark:text-light/70 font-mono leading-relaxed relative overflow-hidden">
                                {template.subject && (
                                    <p className="text-xs font-bold mb-2 text-darkTeal/50 dark:text-light/50 truncate">Subject: {template.subject}</p>
                                )}
                                <p className="line-clamp-4 whitespace-pre-wrap">
                                    {stripHtml(template.message_body).split(/(\{.*?\})/).map((part, i) =>
                                        part.match(/^\{.*\}$/)
                                            ? <span key={i} className="text-primary dark:text-cyan font-bold">{part}</span>
                                            : part
                                    )}
                                </p>
                            </div>

                            <div className="pt-4 border-t border-teal/10 dark:border-light/10 flex items-center justify-between mt-auto">
                                <span className="text-xs text-darkTeal/40 dark:text-light/40 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">history</span> Used {template.usage_count || 0} times
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => handleDuplicate(template)} className="p-1.5 rounded-lg border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/10 dark:hover:bg-cyan/10 transition-colors" title="Duplicate">
                                        <span className="material-symbols-outlined text-lg">content_copy</span>
                                    </button>
                                    <button onClick={() => handleOpenModal(template)} className="p-1.5 rounded-lg border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/10 dark:hover:bg-cyan/10 transition-colors" title="Edit">
                                        <span className="material-symbols-outlined text-lg">edit</span>
                                    </button>
                                    <button onClick={() => handleDelete(template._id || template.id)} disabled={deletingId === (template._id || template.id)} className="p-1.5 rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50" title="Delete">
                                        {deletingId === (template._id || template.id) ? (
                                            <ButtonSpinner size={18} className="text-red-500" />
                                        ) : (
                                            <span className="material-symbols-outlined text-lg">delete</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {filteredTemplates.length === 0 && !loading && !error && (
                    <div className="col-span-full">
                        <EmptyState
                            icon="wysiwyg"
                            title="No templates found"
                            description={activeFilter === 'all'
                                ? "Get started by creating your first message template to automate your outreach."
                                : `No ${activeFilter} templates found. Create one to get started.`}
                            buttonText="Create Template"
                            onAction={() => handleOpenModal()}
                        />
                    </div>
                )}
            </div>

            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/80 backdrop-blur-sm">
                    <div className="bg-white dark:bg-dark-800 rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-teal/20 dark:border-white/10 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-teal/10 dark:border-white/5 flex justify-between items-center sticky top-0 bg-white dark:bg-dark-800 z-10 rounded-t-2xl">
                            <h3 className="text-xl font-bold text-darkTeal dark:text-light">
                                {editingTemplate ? 'Edit Template' : 'Create Template'}
                            </h3>
                            <button onClick={handleCloseModal} className="text-darkTeal/50 dark:text-light/50 hover:text-darkTeal dark:hover:text-light">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-left">
                                {/* Left Column: Form & Variable Inputs */}
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-2">Template Name</label>
                                            <input
                                                type="text"
                                                name="template_name"
                                                value={formData.template_name}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Welcome Message"
                                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light placeholder-darkTeal/30 dark:placeholder-light/30"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-2">Channel</label>
                                            <select
                                                name="channel_type"
                                                value={formData.channel_type}
                                                onChange={handleInputChange}
                                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light"
                                            >
                                                <option value="whatsapp">WhatsApp</option>
                                                <option value="sms">SMS</option>
                                                <option value="email">Email</option>
                                            </select>
                                        </div>
                                    </div>

                                    {formData.channel_type === 'email' && (
                                        <div>
                                            <label className="block text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-2">Subject</label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                placeholder="Email Subject Line"
                                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light placeholder-darkTeal/30 dark:placeholder-light/30"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-2">Message Body</label>
                                        {formData.channel_type === 'email' ? (
                                            <textarea
                                                ref={textareaRef}
                                                name="message_body"
                                                value={formData.message_body}
                                                onChange={handleInputChange}
                                                rows="10"
                                                placeholder="Type your email message here. Use {leadName}, {propertyAddress}, etc. for variables. The branded layout is applied automatically when sending."
                                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light placeholder-darkTeal/30 dark:placeholder-light/30 resize-none font-mono"
                                            ></textarea>
                                        ) : (
                                            <textarea
                                                ref={textareaRef}
                                                name="message_body"
                                                value={formData.message_body}
                                                onChange={handleInputChange}
                                                rows="6"
                                                placeholder="Type your message here..."
                                                className="w-full bg-mint dark:bg-navy border border-teal/20 dark:border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light placeholder-darkTeal/30 dark:placeholder-light/30 resize-none font-mono"
                                            ></textarea>
                                        )}
                                        <div className="text-right text-xs text-darkTeal/40 dark:text-light/40 mt-1">
                                            {formData.message_body.length} characters
                                        </div>
                                    </div>

                                    <div className="bg-mint dark:bg-navy p-4 rounded-xl border border-teal/10 dark:border-white/5">
                                        <h4 className="text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-3">Available Variables</h4>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {['{leadName}', '{propertyAddress}', '{agentName}', '{calendarLink}', '{companyName}'].map(variable => (
                                                <button
                                                    key={variable}
                                                    onClick={() => insertVariable(variable)}
                                                    className="px-2 py-1 bg-white dark:bg-dark-800 border border-teal/20 dark:border-white/10 hover:border-primary dark:hover:border-cyan text-xs font-mono text-darkTeal dark:text-light rounded transition-colors"
                                                >
                                                    {variable}
                                                </button>
                                            ))}
                                        </div>

                                        <h4 className="text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-3 pt-2 border-t border-teal/5 dark:border-white/5">Components</h4>
                                        <button
                                            onClick={insertCTA}
                                            className="px-3 py-1.5 bg-white dark:bg-dark-800 border border-teal/20 dark:border-white/10 hover:border-primary dark:hover:border-cyan text-xs font-bold text-darkTeal dark:text-light rounded transition-colors flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-sm text-primary dark:text-cyan">call_to_action</span>
                                            Insert Call to Action (Box)
                                        </button>
                                    </div>
                                </div>

                                {/* Right Column: Live Preview */}
                                <div className="space-y-6 lg:sticky lg:top-0 h-fit">
                                    <div>
                                        <label className="block text-xs font-bold text-darkTeal/70 dark:text-light/70 uppercase tracking-wider mb-2">Live Preview</label>
                                        <div className={`p-4 rounded-lg border border-teal/10 dark:border-white/5 h-full min-h-[400px] overflow-y-auto scroll-smooth ${formData.channel_type === 'whatsapp' ? 'bg-[#e5ddd5] dark:bg-[#0b141a]' : 'bg-white dark:bg-navy'}`}>
                                            <div className={`text-sm p-3 rounded-lg max-w-[90%] shadow-sm ${formData.channel_type === 'whatsapp' ? 'bg-white dark:bg-[#202c33]' : 'bg-mint dark:bg-dark-800'}`}>
                                                {formData.channel_type === 'email' && <div className="font-bold mb-2 border-b pb-2">{formData.subject || '(No Subject)'}</div>}
                                                <div className="whitespace-pre-wrap text-darkTeal dark:text-light prose dark:prose-invert max-w-none prose-sm">
                                                    {(() => {
                                                        if (!formData.message_body) return <span className="text-gray-400 italic">Start typing to see preview...</span>;

                                                        let content = formData.message_body
                                                            .replaceAll('{leadName}', 'John Doe')
                                                            .replaceAll('{propertyAddress}', '123 Main St')
                                                            .replaceAll('{agentName}', 'Alex Morgan')
                                                            .replaceAll('{calendarLink}', '#')
                                                            .replaceAll('{companyName}', 'OffTrigger');

                                                        if (formData.channel_type === 'email') {
                                                            // For email, show plain text preview (HTML is applied at send time)
                                                            return <span className="whitespace-pre-wrap">{content}</span>;
                                                        }
                                                        return content;
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-teal/10 dark:border-white/5 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-dark-800 z-10 rounded-b-2xl">
                            <button
                                onClick={handleCloseModal}
                                className="px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-darkTeal/50 dark:text-light/50 hover:text-darkTeal dark:hover:text-light transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSaving}
                                className="px-6 py-2.5 bg-primary dark:bg-cyan hover:bg-primary/80 dark:hover:bg-cyan/80 text-white dark:text-navy font-bold rounded-lg shadow-lg shadow-teal/20 dark:shadow-cyan/20 transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                            >
                                {isSaving ? <ButtonSpinner size={18} /> : <span className="material-symbols-outlined text-lg">save</span>}
                                {isSaving ? 'Saving...' : 'Save Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Templates;
